import { memo, useState } from "react";
import toast from "react-hot-toast";
import { LOGO_ACCEPT, formatLogoUploadError, uploadCompanyLogo } from "./uploadCompanyLogo";
import {
  COMMON_TIMEZONES,
  COMPANY_SIZES,
  ONBOARDING_MODE_FULL,
  ONBOARDING_MODE_LITE,
  expandHex,
} from "./onboardingConfigSchema";

const inputClass = "w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white";

function Field({ label, hint, error, className = "", children }) {
  return (
    <label className={`block text-sm ${className}`.trim()}>
      <span className="font-medium text-slate-700 mb-1 block">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-xs text-red-600">{error}</span> : null}
      {hint && !error ? <span className="mt-1 block text-[11px] text-slate-400">{hint}</span> : null}
    </label>
  );
}

function ColorField({ label, value, disabled, error, onChange }) {
  const preview = /^#[0-9a-f]{6}$/.test(expandHex(value)) ? expandHex(value) : "#004f73";
  return (
    <Field label={label} error={error}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={preview}
          disabled={disabled}
          aria-label={`Pick ${label.toLowerCase()}`}
          className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-slate-200 bg-white p-0.5 disabled:opacity-50"
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          className={`${inputClass} font-mono`}
          value={value}
          disabled={disabled}
          placeholder="#004f73"
          maxLength={7}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            const next = expandHex(e.target.value);
            if (/^#[0-9a-f]{6}$/.test(next)) onChange(next);
          }}
        />
      </div>
    </Field>
  );
}

function TenantOnboardingForm({
  form,
  fieldErrors,
  disabled,
  dirty,
  saveBlocked,
  saving,
  onPatch,
  onSave,
}) {
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const busy = disabled || logoUploading;

  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    try {
      const url = await uploadCompanyLogo(file);
      if (!url) throw new Error("Upload finished but no image URL was returned.");
      setLogoBroken(false);
      onPatch({ logoUrl: url });
      toast.success("Logo ready — save onboarding to publish");
    } catch (err) {
      toast.error(formatLogoUploadError(err, { fallback: "Could not upload logo" }));
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Branding</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <span className="font-medium text-slate-700 mb-1 block text-sm">Logo</span>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {form.logoUrl && !logoBroken ? (
                  <img
                    src={form.logoUrl}
                    alt=""
                    className="h-full w-full object-contain p-1"
                    onError={() => setLogoBroken(true)}
                  />
                ) : (
                  <span className="px-1 text-center text-[10px] text-slate-400">No logo</span>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-lg border border-[#004f73]/25 bg-[#004f73]/5 px-3 py-1.5 text-xs font-medium text-[#004f73] disabled:opacity-50"
                    onClick={() => document.getElementById("tenant-onboarding-logo-input")?.click()}
                  >
                    {logoUploading ? "Uploading…" : form.logoUrl ? "Replace image" : "Upload image"}
                  </button>
                  {form.logoUrl ? (
                    <button
                      type="button"
                      disabled={busy}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 disabled:opacity-50"
                      onClick={() => {
                        setLogoBroken(false);
                        onPatch({ logoUrl: "" });
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                  <input
                    id="tenant-onboarding-logo-input"
                    type="file"
                    className="hidden"
                    accept={LOGO_ACCEPT}
                    onChange={handleLogoFile}
                  />
                </div>
                <input
                  className={inputClass}
                  value={form.logoUrl}
                  disabled={busy}
                  placeholder="https://… or /path/to/logo.png"
                  spellCheck={false}
                  onChange={(e) => {
                    setLogoBroken(false);
                    onPatch({ logoUrl: e.target.value });
                  }}
                />
                {fieldErrors.logoUrl ? (
                  <span className="block text-xs text-red-600">{fieldErrors.logoUrl}</span>
                ) : null}
              </div>
            </div>
          </div>
          <ColorField
            label="Primary color"
            value={form.primaryColor}
            disabled={busy}
            error={fieldErrors.primaryColor}
            onChange={(primaryColor) => onPatch({ primaryColor })}
          />
          <ColorField
            label="Secondary color"
            value={form.secondaryColor}
            disabled={busy}
            error={fieldErrors.secondaryColor}
            onChange={(secondaryColor) => onPatch({ secondaryColor })}
          />
          <Field label="Welcome message" error={fieldErrors.welcomeMessage} className="sm:col-span-2">
            <textarea
              className={`${inputClass} min-h-[88px] sm:col-span-2`}
              value={form.welcomeMessage}
              disabled={busy}
              maxLength={2000}
              onChange={(e) => onPatch({ welcomeMessage: e.target.value })}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-3 border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Onboarding mode</p>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-6 text-sm">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="onboardingMode"
              checked={form.onboardingMode === ONBOARDING_MODE_LITE}
              disabled={busy}
              onChange={() => onPatch({ onboardingMode: ONBOARDING_MODE_LITE })}
            />
            <span>
              <span className="font-medium text-slate-900">LITE</span>
              <span className="block text-xs text-slate-500">Short setup for a small team.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="onboardingMode"
              checked={form.onboardingMode === ONBOARDING_MODE_FULL}
              disabled={busy}
              onChange={() => onPatch({ onboardingMode: ONBOARDING_MODE_FULL })}
            />
            <span>
              <span className="font-medium text-slate-900">Full</span>
              <span className="block text-xs text-slate-500">Detailed branding and organization setup.</span>
            </span>
          </label>
        </div>
        {fieldErrors.onboardingMode ? (
          <p className="text-xs text-red-600">{fieldErrors.onboardingMode}</p>
        ) : null}
      </div>

      <div className="space-y-3 border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Organization</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Industry" error={fieldErrors.industry} hint="Used when generating suggestions.">
            <input
              className={inputClass}
              value={form.industry}
              disabled={busy}
              maxLength={80}
              onChange={(e) => onPatch({ industry: e.target.value })}
            />
          </Field>
          <Field label="Company size" error={fieldErrors.companySize}>
            <select
              className={inputClass}
              value={form.companySize}
              disabled={busy}
              onChange={(e) => onPatch({ companySize: e.target.value })}
            >
              <option value="">Not set</option>
              {COMPANY_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Timezone" error={fieldErrors.timezone}>
            <select
              className={inputClass}
              value={form.timezone}
              disabled={busy}
              onChange={(e) => onPatch({ timezone: e.target.value })}
            >
              <option value="">Not set</option>
              {COMMON_TIMEZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Admin contact email" error={fieldErrors.adminContactEmail}>
            <input
              type="email"
              className={inputClass}
              value={form.adminContactEmail}
              disabled={busy}
              maxLength={120}
              onChange={(e) => onPatch({ adminContactEmail: e.target.value })}
            />
          </Field>
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">
          {saveBlocked
            ? "Accept or reject each AI suggestion before saving."
            : dirty
              ? "Unsaved onboarding changes."
              : "Saved settings are live."}
        </p>
        <button
          type="submit"
          disabled={busy || !dirty || saveBlocked}
          className="rounded-xl bg-[#004f73] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save onboarding"}
        </button>
      </div>
    </form>
  );
}

export default memo(TenantOnboardingForm);
